import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import dayjs from 'dayjs';
import { StatusButton, SortableList, Typography } from "../share";
import { DeleteActionIcon, UnderlineText } from '../share/Table';
import constants from "../../constants";

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";

const reorderSchema = Joi.object({
    plan: Joi.any(),
})

const PlanSortableList = forwardRef((props, ref) => {
    const {
        data=undefined,
        defaultColumn=[], 
        statusMutation, 
        canView, 
        canModify, 
        navigateToPlanDetail, 
        navigateToClonePlan, 
        handleOnDeletePlan, 
        hadnleOnArchivePlan,
        onSuccessChangeStatus,
        onFailChangeStatus,
    } = props;

    const { control, handleSubmit, watch, formState: {errors, dirtyFields }, reset, setValue, getValues } = useForm({
        resolver: joiResolver(reorderSchema),
    })

    useEffect(() => {
        if (data) {
            reset({ plan: data })
        }
    }, [data])

    useImperativeHandle(ref, () => ({
        handleSubmit: handleSubmit,
    }));

    const EllipsisTypography = (props) => {
        return (
            <Typography fontSize={14} style={{ height: "fit-content", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                {props.children}
            </Typography>
        )
    }

    const columns = useMemo(() => {
        const statusField = defaultColumn.find(item => item.field === 'status') || {}
        const actionField = defaultColumn.find(item => item.field === 'actions') || {}

        return [
            { dataKey: '_id', label: 'Seq', width: 50, sortable: false },
            { dataKey: 'planId', label: 'PlanID', width: 150, sortable: false, valueGetter: (params) => params.planId },
            { dataKey: 'planName.en', label: 'Plan Name(English)', width: 350, renderCell: (params) => (<EllipsisTypography>{params.planName?.en}</EllipsisTypography>) },
            { dataKey: 'planName.zh', label: 'Plan Name(Chinese)', width: 350, renderCell: (params) => (<EllipsisTypography>{params.planName?.zh}</EllipsisTypography>) },
            { dataKey: 'company.name', label: 'Company Name', width: 160, valueGetter: (params) => params.company },
            // { dataKey: 'publishStartDate', label: 'Start Date & Time', width: 160, valueGetter: (params) => params.value ? dayjs(params.value).format(constants.DATE_TIME_FORMAT) : "" },
            // { dataKey: 'publishEndDate', label: 'End Date & Time', width: 160, valueGetter: (params) => params.value ? dayjs(params.value).format(constants.DATE_TIME_FORMAT) : "" },
            { dataKey: 'originalPrice', label: 'Original Price', width: 140, },
            { dataKey: 'discountedPrice', label: 'Discounted Price', width: 140 },
            {
                dataKey: 'status',
                label: 'Status',
                width: statusField.width || 130,
                sortable: statusField.sortable || false,
                renderCell: (params) => {
                    const { _id: id, status: value } = params

                    const onChange = (newStatusValue) => {
                        statusMutation.mutate(
                            {
                                id,
                                payload: { status: newStatusValue }
                            },
                            {
                                onSuccess: () => {
                                    onSuccessChangeStatus && onSuccessChangeStatus()
                                },
                                onError: () => {
                                    onFailChangeStatus && onFailChangeStatus()
                                }
                            }
                        )
                    }

                    return (
                        <StatusButton
                            key={`${id}-status`}
                            value={value}
                            onChange={onChange}
                            readOnly={!canModify}
                            options={[
                                {
                                    name: "Draft",
                                    id: "draft",
                                    color: "#4BA28D",
                                    Icon: (<EditNoteIcon />)
                                },
                                {
                                    name: "Pending",
                                    id: "pending",
                                    color: "#5D4986",
                                    Icon: (<CheckCircleOutlineIcon />)
                                },
                                {
                                    name: "Active",
                                    id: "active",
                                    // id: "published",
                                    color: "#306097",
                                    canEdit: true,
                                    Icon: (<CheckCircleOutlineIcon />)
                                },
                                {
                                    name: "Suspended",
                                    id: "suspended",
                                    color: "#f6f6f6",
                                    textColor: "#9B9B9B",
                                    canSelect: true,
                                    Icon: (<RemoveCircleOutlineIcon />)
                                },
                                {
                                    name: "Expired",
                                    id: "expired",
                                    color: "#8D9A66",
                                    Icon: (<CheckCircleOutlineIcon />)
                                },
                            ]}
                        />
                    )
                }
            },
            {
                dataKey: 'actions',
                label: 'Actions',
                width: actionField.width || 140,
                sortable: actionField.sortable || false,
                type: 'actions',
                getActions: (params) => {
                    console.log("params", params)
                    const isDeleted = params?.isDeleted === true
                    if (isDeleted) {
                        return [
                            <UnderlineText onClick={() => {
                                hadnleOnArchivePlan(params._id)
                            }} disabled={false}>Archive</UnderlineText>
                        ]
                    }

                    const canDelete = (params?.status === 'draft' || params?.status === 'suspended') ? true : false
                    return [
                        <UnderlineText onClick={() => navigateToPlanDetail(params._id)} disabled={!canView}>View</UnderlineText>,
                        <UnderlineText onClick={() => navigateToClonePlan(params._id)} disabled={!canModify}>Clone</UnderlineText>,
                        <DeleteActionIcon disabled={!canModify} hide={!canDelete} onClick={() => handleOnDeletePlan(params._id)} />
                    ]
                },
            }
        ]
    }, [defaultColumn])

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                overflowX: "auto",
                overflowY: "hidden",
            }}
        >
            <Controller
                name="plan"
                control={control}
                render={({ field }) => {
                    return (
                        <SortableList
                            data={field.value || []}
                            columns={columns}
                            rowHeight={52}
                            containerStyle={{
                                overflowX: "auto",
                                overflowY: "hidden",
                                width: "fit-content",
                            }}
                            headerStyle={{
                                background: "#FFFBE0",
                                borderRadius: 5
                            }}
                            rowStyle={{
                                borderBottom: `1px dashed #E2EBF4`,
                            }}
                            onSort={(oldIndex, newIndex) => {
                                const newItems = field.value.slice();
                                newItems.splice(newIndex, 0, newItems.splice(oldIndex, 1)[0]);
                                // setValues(newItems);
                                field.onChange(newItems)
                            }}
                        />
                    )
                }}
            />
        </div>
    )
});

export default PlanSortableList;